import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { useUnreadNotificationCount } from "@/hooks/use-notifications";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface NotificationBellProps {
  userId: string | null;
  className?: string;
  /** Compact chrome (h-8 button, smaller icon + badge) for dense surfaces like the sidebar footer. */
  compact?: boolean;
}

export const NotificationBell = ({ userId, className, compact = false }: NotificationBellProps) => {
  // Badge only: one HEAD count, not the 50-row notification list.
  const { unreadCount } = useUnreadNotificationCount(userId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Listen for notifications update events and refresh the badge
  useEffect(() => {
    const handleNotificationsUpdate = () => {
      if (userId) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.userNotifications.all });
      }
    };

    // Custom event name for notifications update
    const NOTIFICATIONS_UPDATED_EVENT = "notifications-updated";
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdate);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdate);
    };
  }, [userId, queryClient]);

  if (!userId) {
    return null;
  }

  return (
    <NotificationsDropdown userId={userId}>
      <Button
        variant="ghost"
        size="icon"
        className={cn("relative", compact && "h-8 w-8", className)}
        aria-label={t("pages.notifications.header", "Notifications") + (unreadCount > 0 ? ` (${unreadCount})` : "")}
      >
        <Bell className={compact ? "h-4 w-4" : "h-5 w-5"} />
        {unreadCount > 0 && (
          <Badge
            variant="default"
            className={
              compact
                ? "absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center px-0.5 text-[11px] font-bold rounded-full"
                : "absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs font-bold rounded-full"
            }
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </Badge>
        )}
      </Button>
    </NotificationsDropdown>
  );
};

